import "./App.css";

import { lazy, Suspense, useLayoutEffect } from "react";
import {
    Route,
    BrowserRouter as Router,
    Routes,
    useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { Navbar } from "./components/Navbar/Navbar";
import { SiteSearch } from "./components/Search/SiteSearch";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";

// Eagerly loaded — always needed
import { Home } from "./pages/Home";
import { PrivacyPolicy } from "./components/PrivacyPolicy/PrivacyPolicy";

// Lazily loaded — split into separate chunks
const API = lazy(() => import("./pages/API").then((m) => ({ default: m.API })));
const FAQ = lazy(() => import("./pages/FAQ").then((m) => ({ default: m.FAQ })));
const Guides = lazy(() =>
    import("./pages/Guides").then((m) => ({ default: m.Guides })),
);
const SignUp = lazy(() =>
    import("./pages/SignUp").then((m) => ({ default: m.SignUp })),
);
const UnknownURL = lazy(() =>
    import("./pages/UnknownURL").then((m) => ({ default: m.UnknownURL })),
);
const Licenses = lazy(() =>
    import("./pages/Licenses/Licenses").then((m) => ({ default: m.Licenses })),
);
const ProductLicenses = lazy(() =>
    import("./pages/Licenses/ProductLicenses").then((m) => ({
        default: m.ProductLicenses,
    })),
);

const Manual = lazy(() =>
    import("./pages/Manual/Manual").then((m) => ({ default: m.Manual })),
);
const Introduction = lazy(() =>
    import("./pages/Manual/Introduction").then((m) => ({
        default: m.Introduction,
    })),
);
const Prerequisites = lazy(() =>
    import("./pages/Manual/Prerequisites").then((m) => ({
        default: m.Prerequisites,
    })),
);
const Basics = lazy(() =>
    import("./pages/Manual/Basics").then((m) => ({ default: m.Basics })),
);
const ForestManagementPlanning = lazy(() =>
    import("./pages/Manual/ForestManagementPlanning").then((m) => ({
        default: m.ForestManagementPlanning,
    })),
);
const ActionPlanning = lazy(() =>
    import("./pages/Manual/ActionPlanning").then((m) => ({
        default: m.ActionPlanning,
    })),
);
const DroneOperations = lazy(() =>
    import("./pages/Manual/DroneOperations").then((m) => ({
        default: m.DroneOperations,
    })),
);
const Advanced = lazy(() =>
    import("./pages/Manual/Advanced").then((m) => ({ default: m.Advanced })),
);

const Fire = lazy(() =>
    import("./pages/Landing/Fire").then((m) => ({ default: m.Fire })),
);
const BarkBeetle = lazy(() =>
    import("./pages/Landing/BarkBeetle").then((m) => ({
        default: m.BarkBeetle,
    })),
);
const Windthrow = lazy(() =>
    import("./pages/Landing/Windthrow").then((m) => ({ default: m.Windthrow })),
);
const Wildlife = lazy(() =>
    import("./pages/Landing/Wildlife").then((m) => ({ default: m.Wildlife })),
);
const FinalFellingDronePraxis = lazy(() =>
    import("./pages/Landing/FinalFellingDronePraxis").then((m) => ({
        default: m.FinalFellingDronePraxis,
    })),
);
const PlanningDronePraxis = lazy(() =>
    import("./pages/Landing/PlanningDronePraxis").then((m) => ({
        default: m.PlanningDronePraxis,
    })),
);
const Pilot = lazy(() =>
    import("./pages/Landing/Pilot").then((m) => ({ default: m.Pilot })),
);
const SFABForestManagementPlanning = lazy(() =>
    import("./pages/Landing/SFABForestManagementPlanning").then((m) => ({
        default: m.SFABForestManagementPlanning,
    })),
);
const StoraEnsoBarkBeetle = lazy(() =>
    import("./pages/Landing/StoraEnsoBarkBeetle").then((m) => ({
        default: m.StoraEnsoBarkBeetle,
    })),
);
const KopparforsWindfall = lazy(() =>
    import("./pages/Landing/KopparforsWindfall").then((m) => ({
        default: m.KopparforsWindfall,
    })),
);
const Retailers = lazy(() =>
    import("./pages/Landing/Retailers").then((m) => ({ default: m.Retailers })),
);

const ScrollToTopOnRouteChange = ({ children }) => {
    const location = useLocation();
    useLayoutEffect(() => {
        document.documentElement.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant",
        });
    }, [location.pathname]);
    return children;
};

function App() {
    return (
        <HelmetProvider>
            <div className="flex flex-col h-full">
                <Router>
                    <Navbar />
                    <SiteSearch />
                    <ErrorBoundary>
                        <ScrollToTopOnRouteChange>
                            <Suspense
                                fallback={<div className="min-h-screen" />}
                            >
                                <Routes>
                                    <Route
                                        path="/"
                                        element={<Home />}
                                    />
                                    <Route
                                        path="/manual"
                                        element={<Manual />}
                                    >
                                        <Route
                                            path="introduction"
                                            element={<Introduction />}
                                        />
                                        <Route
                                            path="prerequisites"
                                            element={<Prerequisites />}
                                        />
                                        <Route
                                            path="basics"
                                            element={<Basics />}
                                        />
                                        <Route
                                            path="forest-management-planning"
                                            element={
                                                <ForestManagementPlanning />
                                            }
                                        />
                                        <Route
                                            path="action-planning"
                                            element={<ActionPlanning />}
                                        />
                                        <Route
                                            path="drone-operations"
                                            element={<DroneOperations />}
                                        />
                                        <Route
                                            path="advanced"
                                            element={<Advanced />}
                                        />
                                    </Route>
                                    <Route
                                        path="/guides"
                                        element={<Guides />}
                                    />
                                    <Route
                                        path="/api"
                                        element={<API />}
                                    />
                                    <Route
                                        path="/faq"
                                        element={<FAQ />}
                                    />
                                    <Route
                                        path="/index"
                                        element={<Home />}
                                    />
                                    <Route
                                        path="/index_sv"
                                        element={<Home />}
                                    />
                                    <Route
                                        path="/guides_sv"
                                        element={<Introduction />}
                                    />
                                    <Route
                                        path="/privacy"
                                        element={<PrivacyPolicy />}
                                    />
                                    <Route
                                        path="/privacy_en"
                                        element={<PrivacyPolicy />}
                                    />
                                    <Route
                                        path="/privacy_sv"
                                        element={<PrivacyPolicy />}
                                    />
                                    <Route
                                        path="/licenses"
                                        element={<Licenses />}
                                    />
                                    <Route
                                        path="/licenses/:product"
                                        element={<ProductLicenses />}
                                    />
                                    <Route
                                        path="/sign-up"
                                        element={<SignUp />}
                                    />
                                    <Route
                                        path="/fire"
                                        element={<Fire />}
                                    />
                                    <Route
                                        path="/bark-beetle"
                                        element={<BarkBeetle />}
                                    />
                                    <Route
                                        path="/windthrow"
                                        element={<Windthrow />}
                                    />
                                    <Route
                                        path="/wildlife"
                                        element={<Wildlife />}
                                    />
                                    <Route
                                        path="/final-felling-drone-praxis"
                                        element={<FinalFellingDronePraxis />}
                                    />
                                    <Route
                                        path="/planning-drone-praxis"
                                        element={<PlanningDronePraxis />}
                                    />
                                    <Route
                                        path="/pilot"
                                        element={<Pilot />}
                                    />
                                    <Route
                                        path="/sfab-forest-management-planning"
                                        element={
                                            <SFABForestManagementPlanning />
                                        }
                                    />
                                    <Route
                                        path="/stora-enso-bark-beetle"
                                        element={<StoraEnsoBarkBeetle />}
                                    />
                                    <Route
                                        path="/kopparfors-windfall"
                                        element={<KopparforsWindfall />}
                                    />
                                    <Route
                                        path="/retailers"
                                        element={<Retailers />}
                                    />
                                    <Route
                                        path="*"
                                        exact={true}
                                        element={<UnknownURL />}
                                    />
                                </Routes>
                            </Suspense>
                        </ScrollToTopOnRouteChange>
                    </ErrorBoundary>
                </Router>
            </div>
        </HelmetProvider>
    );
}

export default App;
